import { motion, AnimatePresence } from 'framer-motion';
import { Play, Eye } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { openWhatsApp } from '../../utils/whatsapp';

export default function PortfolioSection() {
  const siteConfig = useSiteConfig();
  const { portfolio, contact } = siteConfig;
  const [activePlatform, setActivePlatform] = useState('all');
  const [ctaLabel, setCtaLabel] = useState(portfolio.ctaText);

  const filteredItems = useMemo(() => {
    if (activePlatform === 'all') return portfolio.items;
    return portfolio.items.filter((item) => item.platform === activePlatform);
  }, [activePlatform, portfolio.items]);

  const handleCta = () => {
    setCtaLabel('⏳ 正在開啟 WhatsApp...');
    openWhatsApp(contact.whatsapp, portfolio.whatsappMessage);
  };

  return (
    <section id="portfolio" className="py-20 md:py-28 bg-deep-black relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {portfolio.sectionTitle}
          </h2>
          <p className="text-base md:text-lg text-gray-400">{portfolio.subtitle}</p>
        </motion.div>

        {/* Platform Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
          {portfolio.platforms.map((platform) => (
            <button
              key={platform.id}
              onClick={() => setActivePlatform(platform.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${
                activePlatform === platform.id
                  ? 'bg-gradient-to-r from-neon-cyan to-neon-purple text-deep-navy border-transparent shadow-neon-cyan'
                  : 'text-gray-400 border-white/10 hover:border-neon-cyan/40 hover:text-neon-cyan'
              }`}
            >
              {platform.label}
            </button>
          ))}
        </div>

        {/* Portfolio grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activePlatform}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 mb-12"
          >
            {filteredItems.map((item, index) => (
              <motion.a
                key={item.id}
                href={item.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="group relative block rounded-2xl overflow-hidden neon-card aspect-[9/16]"
              >
                {item.thumbnail ? (
                  <img
                    src={item.thumbnail}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-neon-purple/40 via-deep-navy to-neon-cyan/30" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-deep-navy via-deep-navy/20 to-transparent" />

                {/* Play icon overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-11 h-11 rounded-full bg-neon-cyan/90 flex items-center justify-center">
                    <Play size={18} className="text-deep-navy ml-0.5" />
                  </div>
                </div>

                {/* Info */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white text-xs md:text-sm font-semibold leading-tight line-clamp-2 mb-1">
                    {item.title}
                  </p>
                  <div className="flex items-center gap-1 text-neon-cyan text-[11px]">
                    <Eye size={12} />
                    <span>{item.views}</span>
                  </div>
                </div>
              </motion.a>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <button
            onClick={handleCta}
            className="inline-block px-8 py-4 bg-gradient-to-r from-neon-purple to-neon-pink text-white font-bold rounded-full transition-all duration-300 hover:brightness-110 hover:-translate-y-0.5"
          >
            {ctaLabel}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
